/**
 * ============================================
 * CONNECTION CONTEXT
 * ============================================
 *
 * Monitors backend connection status.
 * Shows the DisconnectedOverlay only on real outages, not on:
 *   - Tab backgrounded -> setInterval throttled by the browser
 *   - Wifi handoff / ALB idle timeout / DNS blip (1-2 failed checks in a row)
 *   - First post-resume check (stale connection in the OS networking stack)
 *
 * Strategy:
 *   - Periodic health check every 10s while connected.
 *   - On failure: increment consecutive-failures. Show overlay only at >=3
 *     consecutive failures (was 2 -- bumped after user reports of false
 *     positives on long-running tabs).
 *   - First failure schedules a fast 1.5s recheck (not the full 5s) so a
 *     transient blip clears before we ever paint the overlay.
 *   - On `visibilitychange` (tab becomes visible): clear the failure counter
 *     and check immediately. Browsers throttle/pause setInterval in
 *     background tabs, so the first check after resume is the one most
 *     likely to fail spuriously -- treat it as a fresh start.
 *   - On `online` event: same -- network just came back, give it a check
 *     and reset the counter.
 *   - On `offline` event: don't bother checking until we're online again.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { BACKEND_URL } from '@/config';

interface ConnectionState {
  isConnected: boolean;
  isChecking: boolean;
  lastError: string | null;
  retryCount: number;
  nextRetryIn: number;
  isInitializing: boolean;
}

interface ConnectionContextType extends ConnectionState {
  checkConnection: () => Promise<boolean>;
  reportConnectionError: () => void;
}

const ConnectionContext = createContext<ConnectionContextType | null>(null);

const RETRY_INTERVAL_MS = 5000;            // After overlay shown: 5s between retries
const FAST_RECHECK_MS = 1500;              // After first failure: 1.5s recheck
const HEALTH_CHECK_INTERVAL_MS = 10000;    // Background heartbeat while connected
const INITIAL_GRACE_PERIOD_MS = 3000;      // Wait before first check after mount
const HEALTH_REQUEST_TIMEOUT_MS = 8000;    // Per-request abort timeout
const CONSECUTIVE_FAILURES_THRESHOLD = 3;  // Hard threshold before overlay

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConnectionState>({
    // Assume connected until proven otherwise; the initial check happens
    // after INITIAL_GRACE_PERIOD_MS, by which time the dashboard has
    // already painted its first data fetch.
    isConnected: true,
    isChecking: false,
    lastError: null,
    retryCount: 0,
    nextRetryIn: 0,
    isInitializing: true,
  });

  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const fastRecheckRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const healthCheckRef = useRef<NodeJS.Timeout | null>(null);
  const consecutiveFailuresRef = useRef(0);
  // Latched copy of isConnected for use inside event listeners that
  // mount once and need to read current state without re-binding.
  const isConnectedRef = useRef(true);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    // If the OS says we're offline, jump straight to disconnected. The
    // fetch would fail immediately anyway; this just skips the counter
    // dance so the overlay appears instantly and not after 3 fast retries.
    // The `online` event handler will reset the counter and re-check
    // when connectivity returns.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      consecutiveFailuresRef.current = CONSECUTIVE_FAILURES_THRESHOLD;
      isConnectedRef.current = false;
      setState(prev => ({
        isConnected: false,
        isChecking: false,
        lastError: 'Browser is offline',
        retryCount: prev.isConnected ? 1 : prev.retryCount,
        nextRetryIn: RETRY_INTERVAL_MS / 1000,
        isInitializing: false,
      }));
      return false;
    }

    setState(prev => ({ ...prev, isChecking: true }));

    let timeoutId: NodeJS.Timeout | null = null;
    try {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), HEALTH_REQUEST_TIMEOUT_MS);

      // cache: 'no-store' guards against any stale SW or HTTP cache
      // returning a healthy response while the backend is actually down.
      const response = await fetch(`${BACKEND_URL}/api/v1/health`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        cache: 'no-store',
      });

      if (response.ok) {
        consecutiveFailuresRef.current = 0;
        isConnectedRef.current = true;
        setState({
          isConnected: true,
          isChecking: false,
          lastError: null,
          retryCount: 0,
          nextRetryIn: 0,
          isInitializing: false,
        });
        return true;
      }
      throw new Error(`HTTP ${response.status}`);
    } catch (err) {
      consecutiveFailuresRef.current += 1;
      const errorMsg = err instanceof Error ? err.message : 'Connection failed';
      const shouldMarkDisconnected =
        consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_THRESHOLD;

      // Schedule a fast recheck after the FIRST failure -- skip the 5s
      // retry-loop hold so a one-shot blip never paints the overlay.
      if (
        consecutiveFailuresRef.current < CONSECUTIVE_FAILURES_THRESHOLD &&
        fastRecheckRef.current === null
      ) {
        fastRecheckRef.current = setTimeout(() => {
          fastRecheckRef.current = null;
          // Pure call; checkConnection is stable via useCallback.
          checkConnection();
        }, FAST_RECHECK_MS);
      }

      setState(prev => ({
        isConnected: shouldMarkDisconnected ? false : prev.isConnected,
        isChecking: false,
        lastError: errorMsg,
        retryCount: shouldMarkDisconnected
          ? (prev.isConnected ? 1 : prev.retryCount + 1)
          : prev.retryCount,
        nextRetryIn: shouldMarkDisconnected ? RETRY_INTERVAL_MS / 1000 : 0,
        isInitializing: false,
      }));
      if (shouldMarkDisconnected) isConnectedRef.current = false;
      return !shouldMarkDisconnected;
    } finally {
      if (timeoutId !== null) clearTimeout(timeoutId);
    }
  }, []);

  // External components can report a fetch failure they observed. We
  // do NOT call setState side-effects here -- previously this used
  // `setState(prev => { if (prev.isConnected) checkConnection(); ... })`
  // which is a React anti-pattern (updater must be pure; runs twice in
  // strict mode). Just read the latched ref and trigger a check.
  const reportConnectionError = useCallback(() => {
    consecutiveFailuresRef.current += 1;
    if (
      consecutiveFailuresRef.current >= CONSECUTIVE_FAILURES_THRESHOLD &&
      isConnectedRef.current
    ) {
      checkConnection();
    }
  }, [checkConnection]);

  // Periodic health check while connected.
  useEffect(() => {
    if (state.isConnected && !state.isChecking && !state.isInitializing) {
      healthCheckRef.current = setInterval(() => {
        checkConnection();
      }, HEALTH_CHECK_INTERVAL_MS);
    }
    return () => {
      if (healthCheckRef.current) {
        clearInterval(healthCheckRef.current);
        healthCheckRef.current = null;
      }
    };
  }, [state.isConnected, state.isChecking, state.isInitializing, checkConnection]);

  // Retry loop while overlay is visible.
  useEffect(() => {
    if (!state.isConnected && !state.isChecking && !state.isInitializing) {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (healthCheckRef.current) clearInterval(healthCheckRef.current);

      countdownRef.current = setInterval(() => {
        setState(prev => ({
          ...prev,
          nextRetryIn: Math.max(0, prev.nextRetryIn - 1),
        }));
      }, 1000);

      retryTimerRef.current = setTimeout(() => {
        checkConnection();
      }, RETRY_INTERVAL_MS);
    }
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [state.isConnected, state.isChecking, state.isInitializing, checkConnection]);

  // Initial check after the grace period.
  useEffect(() => {
    const graceTimer = setTimeout(() => {
      checkConnection();
    }, INITIAL_GRACE_PERIOD_MS);
    return () => clearTimeout(graceTimer);
  }, [checkConnection]);

  // Visibility / online resilience. Without these, a backgrounded tab
  // returning to focus could trigger Backend Unreachable purely from
  // throttled-setInterval misses + a single stale fetch on resume.
  useEffect(() => {
    const onVisible = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      // Forgive any failure that accumulated while we were hidden; if
      // the next check fails we'll catch it on the normal cadence.
      consecutiveFailuresRef.current = 0;
      // Cancel any pending fast-recheck queued before backgrounding so we
      // don't double-fire.
      if (fastRecheckRef.current) {
        clearTimeout(fastRecheckRef.current);
        fastRecheckRef.current = null;
      }
      checkConnection();
    };

    const onOnline = () => {
      consecutiveFailuresRef.current = 0;
      if (fastRecheckRef.current) {
        clearTimeout(fastRecheckRef.current);
        fastRecheckRef.current = null;
      }
      checkConnection();
    };

    const onOffline = () => {
      // Don't paint overlay just because OS says offline -- our own check
      // will catch it. But cancel pending timers so we don't burn fetches.
      if (fastRecheckRef.current) {
        clearTimeout(fastRecheckRef.current);
        fastRecheckRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [checkConnection]);

  // Cleanup pending fast-recheck on unmount.
  useEffect(() => {
    return () => {
      if (fastRecheckRef.current) {
        clearTimeout(fastRecheckRef.current);
        fastRecheckRef.current = null;
      }
    };
  }, []);

  return (
    <ConnectionContext.Provider value={{ ...state, checkConnection, reportConnectionError }}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnection must be used within ConnectionProvider');
  }
  return context;
}
